import React, {useEffect, useState, useCallback, useRef } from "react";
import { useHistory } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import ApartmentListItemSquare from "./ApartmentListItemSquare";
import ApartmentListItemRow from "./ApartmentListItemRow";
import SkeletonApartmentListItemSquare from "components/Skeletons/SkeletonApartmentListItemSquare";
import SkeletonApartmentListItemRow from "components/Skeletons/SkeletonApartmentListItemRow";
import MultiRangeSlider from "components/Input/MultiRangeSlider";
import Button from "components/Button";
import Selectpicker from "components/Input/Selectpicker";
import ApartmentSelector from "./ApartmentSelector";
import ReactPaginate from 'react-paginate';
import NoDataToDisplay from "components/NoDataToDisplay";

import useWindowSize from 'functions/useWindowSize';
import useDidMountEffect from "functions/useDidMountEffect";
import req from "functions/req";
import clg from "functions/log";

import settings from "configs/settings";

import 'assets/css/ApartmentListings.min.css';  
import { formatOrientation, formatStatus } from "functions/formatDynamicData";
import { encodeNestedObjToQueryString, decodeQueryStringToNestedObj } from "functions/formatQueryString";

import no_image from 'assets/images/csango10-13kerulet-elado-lakasok-4.webp';

/* 
Pagination
https://www.npmjs.com/package/react-paginate
*/

const ApartmentListings = ({viewType, enableViewSelect = true, endpoint = "allProperties", disablePagination = false, disableFilters = false, perPage = 10, isAuthenticated = false }) => {
    
    const history = useHistory();

    const filterRef = useRef(0);

    const defaultFilterConfig =
    {
        "page" : 1,
        "floor" : {
            0 : false,
            1 : false,
            2 : false,
            3 : false,
            4 : false,
            5 : false,
        }, //Selected
        "floor_area" : { min : 30, max : 90, defaultValue : { min : 30, max : 90 } },
        "rooms" : { min : 1, max : 4, defaultValue : { min : 1, max : 4 } },
        "price" : {min : 0, max : 200000000, step: 500000, defaultValue : { min : 0, max : 200000000 }},
        "status" : {
            "options" : [
                { value: formatStatus( 0, "value" ), label: formatStatus( 0, "name" ) },
                { value: formatStatus( 1, "value" ), label: formatStatus( 1, "name" ) },
                { value: formatStatus( 2, "value" ), label: formatStatus( 2, "name" ) },
                { value: formatStatus( 3, "value" ), label: formatStatus( 3, "name" ) },
            ],
            "defaultValue": [
                { value: formatStatus( 0, "value" ), label: formatStatus( 0, "name" ) },
                { value: formatStatus( 1, "value" ), label: formatStatus( 1, "name" ) },
                { value: formatStatus( 2, "value" ), label: formatStatus( 2, "name" ) }, //! 
                { value: formatStatus( 3, "value" ), label: formatStatus( 3, "name" ) },
            ]
        },
        "orientation" : {
            "options" : [
                { value: formatOrientation( "N", "value" ), label: formatOrientation( "N", "label" ) },
                { value: formatOrientation( "S", "value" ), label: formatOrientation( "S", "label" ) },
                { value: formatOrientation( "E", "value" ), label: formatOrientation( "E", "label" ) },
                { value: formatOrientation( "W", "value" ), label: formatOrientation( "W", "label" ) },
            ],
            "defaultValue": [
                { value: formatOrientation( "N", "value" ), label: formatOrientation( "N", "label" ) },
                { value: formatOrientation( "S", "value" ), label: formatOrientation( "S", "label" ) },
                { value: formatOrientation( "E", "value" ), label: formatOrientation( "E", "label" ) },
                { value: formatOrientation( "W", "value" ), label: formatOrientation( "W", "label" ) },
            ]
        },
        "direction" : "ASC", //csökkenő - DESC, növekvő ASC,
        "filterBy" : {
            "options" : [
                { value: "code", label:  "Nincs rendezés" },
                //{ value: "gross_price_ASC", label: "Ár szerint növekvő" },
                //{ value: "gross_price_DESC", label: "Ár szerint csökkenő" },
                { value: "total_area_ASC"/* "floor_area_ASC" */, label:  "Bruttó alapterület szerint növekvő" },
                { value: "total_area_DESC"/* "floor_area_DESC" */, label:  "Bruttó alapterület szerint csökkenő" },
            ],
            "defaultValue": [
                { value: "code", label:  "Nincs rendezés" },
                //{ value: "gross_price_ASC", label: "Ár szerint növekvő" },
            ]
        }   
        //"gross_price", // ár szerint - gross_price, alapterület szerint : floor_area
    };

    const [urlFilters, setUrlFilters] = useState( decodeQueryStringToNestedObj( history.location.search ));

    useEffect(() => {
        if( Object.keys(urlFilters).length > 0 ) {
            //! HA van megadva alapértelmezett szűrési konfig ( query paraméterekben ), akkor beállítás arra.
            //!clg("URL FILTERS: ", urlFilters, defaultFilterConfig);
            setFilterConfig({
                ...defaultFilterConfig,
                page : urlFilters.page,
                floor : {...urlFilters.floor},

                orientation : { //!
                    options : [
                        ...defaultFilterConfig.orientation.options,
                    ],
                    defaultValue : 
                        [...defaultFilterConfig.orientation.options].filter(curr => (
                            urlFilters.orientation.includes( curr.value )
                        )),
                },

                status : { //!
                    options : [
                        ...defaultFilterConfig.status.options,
                    ],
                    defaultValue : 
                        [...defaultFilterConfig.status.options].filter(curr => {
                            if( Array.isArray(urlFilters.status) ) {
                                if( urlFilters.status.includes( curr.value ) ) return curr;    
                            } else {
                                if( urlFilters.status === curr.value ) return curr;
                            }
                            
                        }),
                },

                filterBy : { //!
                    options : [
                        ...defaultFilterConfig.filterBy.options,
                    ],
                    defaultValue : 
                        [...defaultFilterConfig.filterBy.options].filter(curr => (
                            urlFilters.filterBy.includes( curr.value )
                        )),
                },

                rooms : { //!
                    ...defaultFilterConfig.rooms,
                    defaultValue : { min : urlFilters.rooms.min, max : urlFilters.rooms.max}
                },

                floor_area : { //!
                    ...defaultFilterConfig.floor_area,
                    defaultValue : { min : urlFilters.floor_area.min, max : urlFilters.floor_area.max}
                },

                price : { //!
                    ...defaultFilterConfig.price,
                    defaultValue : { min : urlFilters.price.min, max : urlFilters.price.max}
                },


                isDefaultConfig : false
            })
        } else setFilterConfig( {...defaultFilterConfig, isDefaultConfig : true} );
    },[urlFilters])
    
    const [filterConfig, setFilterConfig] = useState({});

    const [filters, setFilters] = useState({
        /* "floor" : filterConfig.floor,
        "floor_area" : filterConfig.floor_area,
        "rooms" : filterConfig.rooms,
        "price" : { min : filterConfig.price.min, max : filterConfig.price.max, },
        "status" : [0,1,3], // <== filterConfig.status.defaultValue values
        "orientation" : ["N","S","E","W"], // <== filterConfig.orieantations.defaultValue values
        "direction" : filterConfig.direction,
        "filterBy" : "code", //"gross_price_ASC", // <== filterConfig.filterBy.defaultValue value */
    });

    useDidMountEffect(() => {
        //!clg("SET FILTERS TO FILTER CONFIG:", filterConfig);
        setFilters({
            page : filterConfig.page,
            filterBy : {...filterConfig.filterBy},
            floor : {...filterConfig.floor},
            status: {...filterConfig.status},
            orientation : {...filterConfig.orientation},
            rooms : {...filterConfig.rooms},
            floor_area : {...filterConfig.floor_area},
            price : {...filterConfig.price},
        })
    },[filterConfig])

    const viewTypeCookie = localStorage.getItem("apartmentListingsViewType");


    const [apartments, setApartments] = useState([]);
    const [nothingToDisplay, setNothingToDisplay] = useState(false);
    const [apartmentsMeta, setApartmentsMeta] = useState({});
    const [viewTypeState, setViewTypeState] = useState( (() => {
        if( !enableViewSelect ) return (viewType ? viewType : "square");
        else return( viewTypeCookie ? viewTypeCookie : (viewType ? viewType : "square") )
    })()) 

    const listingsRef = useRef(null);
    const [filterDebounceValue] = useDebounce(filters, 750); //Filter változtatások esetén várakozik a változások között 750ms-t. Ha ezen az időn belül több módosítás is történik, akkor is csak egyszer fog a "get" callback lefutni

    const windowSize = useWindowSize();

    const get = useCallback((num) => {
        clg("GET: ", num, filters)

        //! Skeleton megjelenítése a töltés idejére

        const filterByDestructed = (() => {
            const v = filters.filterBy.defaultValue[0].value;
            if( v.includes("_ASC") ) {
                const splitted = v.split("_");
                return { filterBy : `${splitted[0]}_${splitted[1]}`, direction : "ASC", }
            } else if( v.includes("_DESC") ) {
                const splitted = v.split("_");
                return { filterBy : `${splitted[0]}_${splitted[1]}`, direction : "DESC", }
            } else {
                return { filterBy : v, direction : "ASC", }
            }
        })();

        const json = {
            page : filters.page,
            direction : filterByDestructed.direction,
            filterBy : filterByDestructed.filterBy,
            floor : filters.floor,
            orientation : [...filters.orientation.defaultValue].map(curr => (
                curr.value
            )),
            status : [...filters.status.defaultValue].map(curr => (
                curr.value
            )),
            rooms : { min : filters.rooms.defaultValue.min, max : filters.rooms.defaultValue.max},
            floor_area : { min : filters.floor_area.defaultValue.min, max : filters.floor_area.defaultValue.max},
            price : { min : filters.price.defaultValue.min, max : filters.price.defaultValue.max},
            perPage : perPage,
        }

        //clg("JSON: ", json);

        let method = "POST";

        if( endpoint === "featuredProperties" ) {
            method = "GET";
        }

        req( { "method" : method, "url" : settings.req[ endpoint ], "data" :  (method === "POST" ? json : null ) } ).then(function(res){
            //clg(settings.req[ endpoint ] + " res", res);
            if( res && res.success ) {
                setApartments( res.data );

                setNothingToDisplay( ( !res.data.length ? true : false ) );

                if( res.hasOwnProperty( "meta" ) ) {
                    setApartmentsMeta( res.meta );
                }
            }
        }).catch(function(err){
            clg("Apartment single res err: ", err);
        });
    }, [endpoint, filterDebounceValue /* filterDebounceValue */ /* filters.floor, filters.price, filters.floor_area, filters.rooms, filters.orientation, filters.status, filters.filterBy, filters.direction */ ]);


    //Scroll top
    //!
    useDidMountEffect(() => {
        if( listingsRef && listingsRef.current ) {
            if( filterRef.current > 0 ) {
                setTimeout(() => {
                    clg("SCROLL", filterRef.current);
                    listingsRef.current.scrollIntoView();
                }, 750);
            }
        }
    },[filters.page]);

    //Oldalbetöltésen kívüli, szűrők, lapozó változása esetén lekérés. debounce: 750ms várakozás van az interakciók között, így csak akkor fog kimenni a kérés, ha nem állít már több szűrőt a felhasználó
    useDidMountEffect(() => {
        //!clg("::", filterRef.current);
        if( filterRef.current >= 0 && filterRef.current !== 1 ) {
            get(2);
        }

        const queryObj = {
            ...filters,
            orientation : [...filters.orientation.defaultValue].map(curr => (
                curr.value
            )),
            status : [...filters.status.defaultValue].map(curr => (
                curr.value
            )),
            filterBy : [...filters.filterBy.defaultValue].map(curr => (
                curr.value
            )),
            rooms : {
                min : filters.rooms.defaultValue.min,
                max : filters.rooms.defaultValue.max,
            },
            floor_area : {
                min : filters.floor_area.defaultValue.min,
                max : filters.floor_area.defaultValue.max,
            },
            price : {
                min : filters.price.defaultValue.min,
                max : filters.price.defaultValue.max,
            },
        }

        //!clg("QUERY OBJ: ", queryObj);
    
        //! Az első felhasználó általi filter változás esetén.
        if( filterRef.current > 1 ) {
            history.push( encodeNestedObjToQueryString(queryObj), "filterChange" );
        }
        filterRef.current = filterRef.current + 1;
    },[filterDebounceValue]);

    function renderViewType( viewType ) {
        switch (viewType) {
            case "row":
                return (
                    apartments.map(curr => (
                        <ApartmentListItemRow
                            id = { curr.id }
                            code = { curr.code }
                            floor = { curr.floor }
                            floor_area = { curr.total_area }//curr.floor_area }
                            rooms = { curr.rooms }
                            images={ (curr.images.length > 0 ? curr.images : [no_image]) }
                            status={ curr.status }
                            orientation={ curr.orientation }
                            price={ curr.gross_price }
                            isAuthenticated={ isAuthenticated }
                            key = { "ApartmentListItemSquare-" + curr.id }
                        />
                    ))
                )

            default:
                return (
                    apartments.map(curr => (
                        <ApartmentListItemSquare
                            id = { curr.id }
                            code = { curr.code }
                            floor = { curr.floor }
                            floor_area = { curr.total_area }//curr.floor_area }
                            rooms = { curr.rooms }
                            images={ (curr.images.length > 0 ? curr.images : [no_image]) }
                            status={ curr.status }
                            orientation={ curr.orientation }
                            price={ curr.gross_price }
                            key = { "ApartmentListItemSquare-" + curr.id }
                        />
                    ))
                )
        }
    }

    const handleFilterChange = ( filterKey, filterData ) => {
        if( filters.hasOwnProperty( filterKey ) ) {
            //clg("HANDLE: ", filterKey, filterData)
            setFilters({
                ...filters,
                [filterKey] : filterData,
            })
        }
    }

    const handleSelectpickerFilterChange = ( fieldName, selected ) => {
        clg("SELECTED: ", selected);

        let selectedValues = [];
        selected.map(curr => {
            selectedValues.push( curr.value ); 
        });

        //clg("SET ORI: ", filters, selectedValues, "OPTS: ", filters.orientation);

            const spreadPrev = (() => {
                if( selectedValues.length > 0 ) {
                    return {
                        defaultValue : [...filters[fieldName].options].filter(curr => (
                            selectedValues.includes( curr.value )
                        )),
                        options : [
                            ...filters[fieldName].options
                        ]
                    }
                } else {
                    return { 
                        defaultValue : [...filters[fieldName].options],
                        options : [...filters[fieldName].options],
                    }
                }
            })(); 
        
        handleFilterChange(fieldName, spreadPrev);
    }

    useEffect(() => {
        if( enableViewSelect ) {
            localStorage.setItem("apartmentListingsViewType", viewTypeState);
        }
    },[viewTypeState])

    return(
        <>
        {
            !disableFilters && (
            <div className="row filter-container">

                <div className="col-lg-8 col-12 dots dots-top-right apartment-selector">
                    {
                        <ApartmentSelector _selected={filters.floor} viewType={windowSize.width >= 992 ? "image" : "buttons"} setFilters={ { function: handleFilterChange, param1 : "floor" } }/>
                    }
                </div>

                <div className="col-lg-4 col-12">

                    {Object.keys(filters).length > 0 && (
                    <>
                     {/*<MultiRangeSlider
                        min={ filters.price.min }
                        max={ filters.price.max }
                        value={ filters.price.defaultValue }
                        col="col-12"
                        title="Ár"
                        step={ filterConfig.price.step }
                        sliderValPostfix={<span> Ft</span>}
                        valFormat="money"
                        onChange={ (values) => setFilters({...filters, price : {...filters.price, defaultValue : { min : values.min, max : values.max }}})}
                    />*/}

                    <MultiRangeSlider
                        min={ filters.floor_area.min }
                        max={ filters.floor_area.max }
                        value={ filters.floor_area.defaultValue }
                        col="col-12"
                        title="Bruttó alapterület"
                        sliderValPostfix={<span> m<sup>2</sup></span>}
                        onChange={ (values) => setFilters({...filters, floor_area : {...filters.floor_area, defaultValue : { min : values.min, max : values.max }}})}
                    />
                     
                    
                    <MultiRangeSlider
                        min={ filters.rooms.min }
                        max={ filters.rooms.max }
                        value={ filters.rooms.defaultValue }
                        col="col-12"
                        title="Szobák száma"
                        onChange={ (values) => setFilters({...filters, rooms : {...filters.rooms, defaultValue : { min : values.min, max : values.max }}})}
                    />

                    
                    <div className="col-12 orientation-filter">
                        <Selectpicker
                            defaultValue={filters.orientation.defaultValue}
                            title="Tájolás"
                            isMulti
                            name="orientation"
                            options={filters.orientation.options}
                            placeholder="Tájolás kiválasztása"
                            className="select-orientation"
                            classNamePrefix="select"
                            onChange={ (selected) => handleSelectpickerFilterChange( "orientation", selected)}
                        />
                    </div>

                    
                    <div className="col-12 status-filter">
                        <Selectpicker
                            defaultValue={filters.status.defaultValue}
                            title="Státusz"
                            isMulti
                            name="status"
                            options={filters.status.options}
                            placeholder="Státusz kiválasztása"
                            className="select-status"
                            classNamePrefix="select"
                            onChange={ (selected) => handleSelectpickerFilterChange( "status", selected)}
                        />
                    </div>
                    </>
                    )}

                    {/* <div className="col-12 filter-button-container">
                        <Button onClick={ () => get() } text="Szűrés" icon="fas fa-search" className="btn-secondary max-width-100 text-sized-icon"/>
                    </div> */}

                </div>

            </div>
        )}
        
        <div className="row mt-5" ref={listingsRef}>


                <div className="col-12 icon-filters">
                    <div className="row">
  
                        <div className={"col-12 col-sm-6 option"}>
                            {(windowSize.width >= 1200 && enableViewSelect) && (
                            <>
                                <i className={"fas fa-th-large" + ( viewTypeState === "square" ? " active" : "" )} title="Rács nézet" onClick={ () => setViewTypeState("square") }></i>
                                <i className={"fas fa-bars"  + ( viewTypeState === "row" ? " active" : "" ) } title="Lista nézet" onClick={ () => setViewTypeState("row") }></i>
                            </>
                            )}
                        </div>
                        
                        {!disableFilters && (
                        <div className={"col-12 col-sm-6 option"}>
            
                            <>
                                {/* <i className={"fas fa-sort-amount-down-alt" + ( filters.direction === "ASC" ? " active" : "" )} title="Ár szerint növekvő rendezés" onClick={ () => handleFilterChange("direction", "ASC") }></i>
                                <i className={"fas fa-sort-amount-down" + ( filters.direction === "DESC" ? " active" : "" )} title="Ár szerint csökkenő rendezés" onClick={ () => handleFilterChange("direction", "DESC") }></i> */}
                                
                                
                                {Object.keys(filters).length > 0 && (
                                <Selectpicker
                                    defaultValue={filters.filterBy.defaultValue}
                                    name="orientation"
                                    options={filters.filterBy.options}
                                    placeholder="Rendezés"
                                    className="select-filter-by"
                                    classNamePrefix="select"
                                    onChange={ (selected) => handleSelectpickerFilterChange( "filterBy", [selected])}
                                />
                                )}
                            </>
                            
                        </div>
                        )}

                    </div>
                </div>

            {(apartments.length > 0 && !nothingToDisplay) && (

            renderViewType( viewTypeState )
            
            )}

            {(apartments.length === 0 && !nothingToDisplay) && (

            [...Array( perPage ).keys()].map(curr => {
                return viewTypeState === "row" ? <SkeletonApartmentListItemRow key={ curr }/> : <SkeletonApartmentListItemSquare key={ curr }/>
            })
            
            )}

            {(apartments.length === 0 && nothingToDisplay) && (
                <NoDataToDisplay text="Nincs megjeleníthető lakás"/>
            )}

            { (!disablePagination && apartments.length > 0) && (
                
                    <ReactPaginate
                        previousLabel={<i className="fas fa-chevron-left"></i>}
                        nextLabel={<i className="fas fa-chevron-right"></i>}
                        breakLabel={'...'}
                        breakClassName={'break-me'}
                        pageCount={ apartmentsMeta.lastPage }
                        marginPagesDisplayed={1}
                        pageRangeDisplayed={windowSize.width < 576 ? 2 : 5}
                        initialPage={ filters.page - 1 }
                        onPageChange={(e) => handleFilterChange("page", (isNaN(e.selected) ? 1 : e.selected + 1) ) }
                        containerClassName={`pagination`} 
                        activeClassName={'active'}
                    />
                
            )}

        </div>

        </>
    )
}

export default ApartmentListings;